import { useState, useCallback, useEffect } from 'react';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'primary' | 'danger';
  loading?: boolean;
  /** If provided, renders a textarea for the user to fill in. */
  noteLabel?: string;
  noteRequired?: boolean;
  onConfirm: (note: string) => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = '确认',
  cancelLabel = '取消',
  variant = 'primary',
  loading = false,
  noteLabel,
  noteRequired = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) setNote('');
  }, [open]);

  const handleConfirm = useCallback(() => {
    onConfirm(note.trim());
  }, [onConfirm, note]);

  const handleCancel = useCallback(() => {
    setNote('');
    onCancel();
  }, [onCancel]);

  if (!open) return null;

  const confirmDisabled = loading || (noteRequired && !note.trim());

  const confirmBg =
    variant === 'danger'
      ? 'bg-red-500 hover:bg-red-600 text-white'
      : 'bg-primary hover:bg-primary/90 text-white';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={loading ? undefined : handleCancel}
      />
      {/* Panel */}
      <div className="relative bg-white rounded-lg shadow-lg w-full max-w-sm mx-4 p-6 space-y-4">
        <h3 className="text-base font-medium text-gray-900">{title}</h3>
        {description && (
          <p className="text-sm text-gray-500">{description}</p>
        )}
        {noteLabel && (
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              {noteLabel}
              {noteRequired && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
              placeholder="请输入..."
            />
          </div>
        )}
        <div className="flex justify-end gap-3 pt-1">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className={`px-4 py-2 text-sm rounded-md disabled:opacity-40 transition-colors flex items-center gap-2 ${confirmBg}`}
          >
            {loading && (
              <span className="h-3.5 w-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
